import { apiClient } from '@/lib/api-client';
import type {
  Contractor,
  ContractorQueryParams,
  CreateContractorInput,
  PaginatedResponse,
  UpdateContractorInput,
} from '@/types/contractor';

// API wraps responses in result object
interface ApiWrapper<T> {
  result: T;
  status: number;
  message: string;
  error: null | { code: number; message: string; issues: unknown[] };
}

export const contractorService = {
  /**
   * Get all contractors with pagination and filters
   */
  async getAll(params?: ContractorQueryParams): Promise<PaginatedResponse<Contractor>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.minRating) queryParams.set('minRating', String(params.minRating));
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const endpoint = `/contractors${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<ApiWrapper<PaginatedResponse<Contractor>>>(endpoint);
    return response.result;
  },

  /**
   * Get active contractors (for dropdowns)
   */
  async getActive(): Promise<Contractor[]> {
    const response = await apiClient.get<ApiWrapper<Contractor[]>>('/contractors/active');
    return response.result;
  },

  /**
   * Get contractor by ID
   */
  async getById(id: string): Promise<Contractor> {
    const response = await apiClient.get<ApiWrapper<Contractor>>(`/contractors/${id}`);
    return response.result;
  },

  /**
   * Create a new contractor
   */
  async create(data: CreateContractorInput): Promise<Contractor> {
    const response = await apiClient.post<ApiWrapper<Contractor>>('/contractors', data);
    return response.result;
  },

  /**
   * Update a contractor
   */
  async update(id: string, data: UpdateContractorInput): Promise<Contractor> {
    const response = await apiClient.put<ApiWrapper<Contractor>>(`/contractors/${id}`, data);
    return response.result;
  },

  /**
   * Delete (soft delete) a contractor
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/contractors/${id}`);
  },
};
